import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LanguageProvider } from './contexts/LanguageContext';
import { StreamStatsProvider } from './contexts/StreamStatsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import EmbeddedPlayer from './components/EmbeddedPlayer';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Channels from './components/Channels';
import VOD from './components/VOD';
import Playlists from './components/Playlists';
import Simulcast from './components/Simulcast';
import Analytics from './components/Analytics';
import Settings from './components/Settings';
import Spots from './components/Spots';
import Avances from './components/Avances';

// 25 minutes of inactivity → auto-logout
const INACTIVITY_TIMEOUT_MS = 25 * 60 * 1000;
const INACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'click'];

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep stable ref to handleLogout so the inactivity effect doesn't need it as dep
  const handleLogoutRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const sessionActive = sessionStorage.getItem('session_active');

    if (token && userData) {
      // Without rememberMe: require session_active marker (cleared when browser closes)
      if (!rememberMe && !sessionActive) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('rememberMe');
        setLoading(false);
        return;
      }
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('session_active');
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  // Keep ref up-to-date
  useEffect(() => {
    handleLogoutRef.current = handleLogout;
  }, [handleLogout]);

  // Auto-logout when StreamStatsContext detects an expired/invalid token (403)
  useEffect(() => {
    const onTokenExpired = () => handleLogoutRef.current();
    window.addEventListener('auth:token-expired', onTokenExpired);
    return () => window.removeEventListener('auth:token-expired', onTokenExpired);
  }, []);

  // Accept rememberMe from Login component
  const handleLogin = (token, userData, rememberMe = false) => {
    // Token always goes to localStorage so all components can read it without changes
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('rememberMe', String(rememberMe));
    if (!rememberMe) {
      // Session marker: cleared automatically when browser/tab closes
      sessionStorage.setItem('session_active', 'true');
    } else {
      sessionStorage.removeItem('session_active');
    }
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleUserUpdate = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // ── Verify token when page becomes visible again (wake from sleep) ──
  useEffect(() => {
    if (!isAuthenticated) return;

    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const token = localStorage.getItem('token');
      if (!token) {
        handleLogoutRef.current();
        return;
      }

      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401 || res.status === 403) {
          handleLogoutRef.current();
          toast.info('Tu sesión expiró. Por favor, inicia sesión de nuevo.', { autoClose: 6000 });
        }
      } catch {
        // Network error — don't logout, user may be offline
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isAuthenticated]);

  // ── Inactivity timeout ──
  useEffect(() => {
    if (!isAuthenticated) return;

    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    if (rememberMe) return; // No timeout when "Recordarme" is checked

    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        handleLogoutRef.current();
        toast.info('Sesión cerrada por inactividad. Por favor, inicia sesión de nuevo.', {
          autoClose: 6000,
        });
      }, INACTIVITY_TIMEOUT_MS);
    };

    INACTIVITY_EVENTS.forEach((evt) =>
      document.addEventListener(evt, resetTimer, { passive: true })
    );
    resetTimer(); // Start the initial countdown

    return () => {
      clearTimeout(timer);
      INACTIVITY_EVENTS.forEach((evt) =>
        document.removeEventListener(evt, resetTimer)
      );
    };
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#0a0a0f'
      }}>
        <div className="loading" style={{ width: '40px', height: '40px' }}></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <StreamStatsProvider>
          <Router>
            <div className="App">
              <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
              />

              <Routes>
                <Route
                  path="/login"
                  element={
                    isAuthenticated ?
                      <Navigate to="/dashboard" /> :
                      <Login onLogin={handleLogin} />
                  }
                />

                <Route
                  path="/dashboard"
                  element={
                    isAuthenticated ?
                      <Dashboard user={user} onLogout={handleLogout} /> :
                      <Navigate to="/login" />
                  }
                />

                <Route
                  path="/channels"
                  element={
                    isAuthenticated ?
                      <Channels user={user} onLogout={handleLogout} /> :
                      <Navigate to="/login" />
                  }
                />

                <Route
                  path="/vod"
                  element={
                    isAuthenticated ?
                      <VOD user={user} onLogout={handleLogout} /> :
                      <Navigate to="/login" />
                  }
                />

                <Route
                  path="/playlists"
                  element={
                    isAuthenticated ?
                      <Playlists user={user} onLogout={handleLogout} /> :
                      <Navigate to="/login" />
                  }
                />

                <Route
                  path="/spots"
                  element={
                    isAuthenticated ?
                      <Spots user={user} onLogout={handleLogout} /> :
                      <Navigate to="/login" />
                  }
                />

                <Route
                  path="/avances"
                  element={
                    isAuthenticated ?
                      <Avances user={user} onLogout={handleLogout} /> :
                      <Navigate to="/login" />
                  }
                />

                <Route
                  path="/simulcast"
                  element={
                    isAuthenticated ?
                      <Simulcast user={user} onLogout={handleLogout} /> :
                      <Navigate to="/login" />
                  }
                />

                <Route
                  path="/analytics"
                  element={
                    isAuthenticated ?
                      <Analytics user={user} onLogout={handleLogout} /> :
                      <Navigate to="/login" />
                  }
                />

                <Route
                  path="/settings"
                  element={
                    isAuthenticated ?
                      <Settings user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> :
                      <Navigate to="/login" />
                  }
                />

                {/* /player is public — EmbeddedPlayer handles auth internally (isPublic flag) */}
                <Route
                  path="/player"
                  element={<EmbeddedPlayer user={user} onLogout={handleLogout} />}
                />
                <Route path="/" element={<Navigate to="/dashboard" />} />
              </Routes>
            </div>
          </Router>
        </StreamStatsProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
